import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Paper, Typography, Box, Tabs, Tab,
  Grid, Card, CardContent, CircularProgress, Alert,
  Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Chip
} from '@mui/material';
import {
  BarChart as StatsIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  CalendarMonth as CalendarIcon,
  TrendingUp as RevenueIcon,
  Discount as DiscountIcon,
  Assignment as OrderIcon,
  Person as PersonIcon,
  EventNote as FollowUpIcon,
  Autorenew as RepeatIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import config from '../config';

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const CATEGORY_COLORS = {
  'New':           '#1976d2',
  'Lead':          '#0288d1',
  'Repeat':        '#388e3c',
  'Reactivated':   '#f57c00',
  'Update':        '#7b1fa2',
  'New to School': '#00897b',
  'Unknown':       '#757575',
};

const DISCOUNT_COLOR = '#e65100';

const CATEGORY_ORDER = ['New', 'Lead', 'Repeat', 'Reactivated', 'Update', 'New to School', 'Unknown'];

// ─── Utility ──────────────────────────────────────────────────────────────────

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
}

function getMonthLabel(year, month) {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

// ─── MonthYearPicker ──────────────────────────────────────────────────────────

function MonthYearPicker({ selectedMonth, selectedYear, filterType, onMonthChange, onYearChange, onFilterTypeChange }) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarYear, setCalendarYear] = useState(parseInt(selectedYear));

  const navigateMonth = (dir) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    onFilterTypeChange('month');
  };

  const displayLabel = filterType === 'month'
    ? getMonthLabel(...selectedMonth.split('-').map(Number))
    : `Year ${selectedYear}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {filterType === 'month' && (
        <IconButton size="small" onClick={() => navigateMonth(-1)}><PrevIcon /></IconButton>
      )}
      <Button
        variant="outlined"
        size="small"
        startIcon={<CalendarIcon />}
        onClick={() => {
          setCalendarYear(filterType === 'year' ? parseInt(selectedYear) : parseInt(selectedMonth));
          setCalendarOpen(true);
        }}
      >
        {displayLabel}
      </Button>
      {filterType === 'month' && (
        <IconButton size="small" onClick={() => navigateMonth(1)}><NextIcon /></IconButton>
      )}

      <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <IconButton size="small" onClick={() => setCalendarYear(y => y - 1)}><PrevIcon /></IconButton>
            <Typography variant="h6">{calendarYear}</Typography>
            <IconButton size="small" onClick={() => setCalendarYear(y => y + 1)}><NextIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {MONTH_NAMES.map((name, idx) => (
              <Grid item xs={4} key={name}>
                <Button
                  fullWidth
                  size="small"
                  variant={
                    filterType === 'month' &&
                    selectedMonth === `${calendarYear}-${String(idx + 1).padStart(2, '0')}`
                      ? 'contained' : 'outlined'
                  }
                  onClick={() => {
                    onMonthChange(`${calendarYear}-${String(idx + 1).padStart(2, '0')}`);
                    onFilterTypeChange('month');
                    setCalendarOpen(false);
                  }}
                >
                  {name.slice(0, 3)}
                </Button>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mb: 2 }} />
          <Button
            fullWidth
            variant={filterType === 'year' && selectedYear === String(calendarYear) ? 'contained' : 'outlined'}
            onClick={() => {
              onYearChange(String(calendarYear));
              onFilterTypeChange('year');
              setCalendarOpen(false);
            }}
          >
            Full Year {calendarYear}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, icon }) {
  return (
    <Card elevation={2} sx={{ height: '100%', borderTop: `4px solid ${color || '#1976d2'}` }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: color || 'text.primary' }}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {sub}
              </Typography>
            )}
          </Box>
          {icon && (
            <Box sx={{ color: color || '#1976d2', opacity: 0.25, fontSize: 40, ml: 1 }}>
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── CategoryBreakdown ────────────────────────────────────────────────────────

function CategoryBreakdown({ categories, totalRevenue, totalOrders }) {
  const sorted = CATEGORY_ORDER.filter(c => categories[c]);
  const others = Object.keys(categories).filter(c => !CATEGORY_ORDER.includes(c));
  const all = [...sorted, ...others];

  if (all.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        No orders in this period
      </Typography>
    );
  }

  // Use highest category revenue as the 100% mark so bars scale relative to each other
  const maxRevenue = Math.max(...all.map(c => categories[c].revenue), 1);

  return (
    <Box>
      {all.map(cat => {
        const { count, revenue, discount = 0, grossRevenue: catGross = 0 } = categories[cat];
        const color = CATEGORY_COLORS[cat] || '#757575';

        const revPct    = totalRevenue > 0 ? ((revenue / totalRevenue) * 100).toFixed(1) : '0.0';
        const orderPct  = totalOrders  > 0 ? ((count   / totalOrders)  * 100).toFixed(0) : '0';
        const discPct   = catGross     > 0 ? ((discount / catGross)     * 100).toFixed(1) : '0.0';

        // Bar widths: revenue fills relative to the biggest category; discount on same scale
        const revBarW  = ((revenue  / maxRevenue) * 100).toFixed(1);
        const discBarW = ((discount / maxRevenue) * 100).toFixed(1);

        return (
          <Box
            key={cat}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: color + '0a',
              border: `1px solid ${color}30`,
            }}
          >
            {/* ── Header ── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Typography variant="body1" fontWeight="bold" sx={{ color }}>
                  {cat}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`${count} order${count !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ bgcolor: color + '20', color, fontWeight: 'bold', fontSize: '0.7rem' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {orderPct}% of orders
                </Typography>
              </Box>
            </Box>

            {/* ── Revenue row ── */}
            <Box sx={{ mb: 1.25 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Revenue
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ color }}>
                    {formatCurrency(revenue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {revPct}% of period
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ height: 7, bgcolor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${revBarW}%`,
                    bgcolor: color,
                    borderRadius: 4,
                    transition: 'width 0.5s ease',
                  }}
                />
              </Box>
            </Box>

            {/* ── Discounts row ── */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Discounts
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: DISCOUNT_COLOR }}>
                    {formatCurrency(discount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {discPct}% of gross
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ height: 4, bgcolor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${discBarW}%`,
                    bgcolor: DISCOUNT_COLOR,
                    borderRadius: 4,
                    transition: 'width 0.5s ease',
                  }}
                />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── PersonStats ──────────────────────────────────────────────────────────────

function PersonStats({ person }) {
  const repeatRevenue  = person.categories?.['Repeat']?.revenue || 0;
  const followUpTotal  = person.followUpTotal  || 0;
  const followUpCount  = person.followUpCount  || 0;

  const conversionPct  = followUpTotal > 0
    ? ((repeatRevenue / followUpTotal) * 100).toFixed(1)
    : null;

  const conversionColor = conversionPct !== null
    ? (parseFloat(conversionPct) >= 100 ? '#2e7d32' : parseFloat(conversionPct) >= 50 ? '#1976d2' : '#e65100')
    : '#757575';

  return (
    <Box>
      {/* ── Row 1: core metrics ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Orders"
            value={person.totalOrders}
            color="#1976d2"
            icon={<OrderIcon fontSize="inherit" />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Revenue"
            value={formatCurrency(person.totalRevenue)}
            color="#388e3c"
            icon={<RevenueIcon fontSize="inherit" />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Discounts"
            value={formatCurrency(person.totalDiscount)}
            color={DISCOUNT_COLOR}
            icon={<DiscountIcon fontSize="inherit" />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Avg Discount"
            value={`${person.avgDiscountPct}%`}
            sub={`${person.pctOrdersDiscounted}% of orders discounted`}
            color="#7b1fa2"
            icon={<DiscountIcon fontSize="inherit" />}
          />
        </Grid>
      </Grid>

      {/* ── Row 2: follow-up metrics ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label="Follow-Ups Due"
            value={formatCurrency(followUpTotal)}
            sub={
              followUpCount > 0
                ? `${followUpCount} order${followUpCount !== 1 ? 's' : ''} ending this period`
                : 'No orders ending this period'
            }
            color="#0288d1"
            icon={<FollowUpIcon fontSize="inherit" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label="Repeat vs Follow-Ups"
            value={conversionPct !== null ? `${conversionPct}%` : '—'}
            sub={
              followUpTotal > 0
                ? `${formatCurrency(repeatRevenue)} Repeat / ${formatCurrency(followUpTotal)} Follow-Up`
                : 'No follow-ups this period'
            }
            color={conversionColor}
            icon={<RepeatIcon fontSize="inherit" />}
          />
        </Grid>
      </Grid>

      {/* ── Order breakdown ── */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Order Breakdown by Type
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <CategoryBreakdown
          categories={person.categories}
          totalRevenue={person.totalRevenue}
          totalOrders={person.totalOrders}
        />
      </Paper>
    </Box>
  );
}

// ─── AllStats (aggregate across all users) ───────────────────────────────────

function AllStats({ data }) {
  const aggregate = {
    salesPersonId: 'all',
    salesPersonName: 'All Sales',
    categories: {},
    totalOrders: 0,
    totalRevenue: 0,
    totalDiscount: 0,
    grossRevenue: 0,
    ordersWithDiscount: 0,
    followUpTotal: 0,
    followUpCount: 0,
  };

  for (const p of data) {
    aggregate.totalOrders      += p.totalOrders;
    aggregate.totalRevenue     += p.totalRevenue;
    aggregate.totalDiscount    += p.totalDiscount;
    aggregate.grossRevenue     += p.grossRevenue;
    aggregate.ordersWithDiscount += p.ordersWithDiscount;
    aggregate.followUpTotal    += p.followUpTotal || 0;
    aggregate.followUpCount    += p.followUpCount || 0;

    for (const [cat, vals] of Object.entries(p.categories)) {
      if (!aggregate.categories[cat]) {
        aggregate.categories[cat] = { count: 0, revenue: 0, discount: 0, grossRevenue: 0 };
      }
      aggregate.categories[cat].count      += vals.count;
      aggregate.categories[cat].revenue    += vals.revenue;
      aggregate.categories[cat].discount   += vals.discount   || 0;
      aggregate.categories[cat].grossRevenue += vals.grossRevenue || 0;
    }
  }

  aggregate.avgDiscountPct = aggregate.grossRevenue > 0
    ? ((aggregate.totalDiscount / aggregate.grossRevenue) * 100).toFixed(1)
    : '0.0';
  aggregate.pctOrdersDiscounted = aggregate.totalOrders > 0
    ? ((aggregate.ordersWithDiscount / aggregate.totalOrders) * 100).toFixed(1)
    : '0.0';

  return <PersonStats person={aggregate} />;
}

// ─── Main Stats Page ──────────────────────────────────────────────────────────

export default function Stats() {
  const { token } = useAuth();

  const now = new Date();
  const initMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(initMonth);
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [filterType, setFilterType] = useState('month');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [y, m] = selectedMonth.split('-').map(Number);
      const params = filterType === 'month'
        ? `?month=${m}&year=${y}&filterType=month`
        : `?year=${selectedYear}&filterType=year`;

      const res = await fetch(`${config.apiUrl}/api/stats/sales${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        setData(json.data);
        setActiveTab(0);
      } else {
        setError(json.message || 'Failed to load stats');
      }
    } catch (err) {
      setError('Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [token, selectedMonth, selectedYear, filterType]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const tabs = [{ label: 'All Sales', id: 'all' }, ...data.map(p => ({
    label: p.salesPersonName || 'Unassigned',
    id: p.salesPersonId,
  }))];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StatsIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">Sales Stats</Typography>
            <Typography variant="body2" color="text.secondary">Ad order performance by sales person</Typography>
          </Box>
        </Box>
        <MonthYearPicker
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          filterType={filterType}
          onMonthChange={setSelectedMonth}
          onYearChange={setSelectedYear}
          onFilterTypeChange={setFilterType}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={2}>
          {/* Person tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            {tabs.map((t, i) => (
              <Tab
                key={t.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {i === 0 ? <StatsIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                    {t.label}
                  </Box>
                }
              />
            ))}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {data.length === 0 && !loading ? (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                No ad orders found for this period.
              </Typography>
            ) : (
              <>
                {activeTab === 0 && <AllStats data={data} />}
                {data.map((person, idx) =>
                  activeTab === idx + 1 ? (
                    <PersonStats key={person.salesPersonId} person={person} />
                  ) : null
                )}
              </>
            )}
          </Box>
        </Paper>
      )}
    </Container>
  );
}
